import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { DateBox } from 'devextreme-react/date-box';
import { NumberBox } from 'devextreme-react/number-box';
import { RadioGroup } from 'devextreme-react/radio-group';
import { SelectBox } from 'devextreme-react/select-box';
import { Button } from 'devextreme-react/button';
import { TextBox } from 'devextreme-react/text-box';
import DataGrid, { Column, Scrolling, Paging } from 'devextreme-react/data-grid';
import { useQuery } from '@tanstack/react-query';
import notify from 'devextreme/ui/notify';
import useApi from '../../hooks/useApi';
import useLookUpBase from '../../components/selectbox/LookupBase';
import Guid from '../../models/Guid';
import ITrancheAge from '../../models/ITrancheAge';
import { STATUT_MEMBRE, STATUT_RESIDENT } from '../gestion-plans/calendrier/constant';
import './gestion-calculateur-tarifs-web.css';

interface IPlanDetailTarifRow {
  description: string;
  dateDebut: Date;
  dateFin: Date;
  idTrancheAge: Guid;
  nombrePersonnes: number;
  duree: number;
  quantite: number;
  prix: number;
  sousTotal: number;
  montantHebergementTaxable: number;
  typeDetail: number;
  ageFin?: number;
}

const TYPE_DETAIL_LABELS: Record<number, string> = {
  0: 'Condition manquante',
  1: 'Séjour',
  2: 'Personne additionnelle',
  3: 'Charge additionnelle',
  4: 'Familial',
  5: 'Plan enfant',
};

const formatTrancheAgeLabel = (tranche: ITrancheAge) => {
  if (tranche.ageFin >= 99) {
    return `${tranche.ageDebut} ans et plus`;
  }
  if (tranche.ageDebut === 0) {
    return `${tranche.ageFin} ans et moins`;
  }
  return `${tranche.ageDebut}-${tranche.ageFin} ans`;
};

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const combineDateAndTime = (date: Date, time: Date) => {
  const result = new Date(date);
  result.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return result;
};

export default function GestionCalculateurTarifsWeb() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [dateDebut, setDateDebut] = useState<Date>(startOfToday);
  const [heureDebut, setHeureDebut] = useState<Date>(startOfToday);
  const [dateFin, setDateFin] = useState<Date>(startOfToday);
  const [heureFin, setHeureFin] = useState<Date>(startOfToday);
  const [statutResident, setStatutResident] = useState(0);
  const [statutMembre, setStatutMembre] = useState(0);
  const [idPlanEntete, setIdPlanEntete] = useState<Guid>(Guid.empty);
  const [idUnite, setIdUnite] = useState<Guid>(Guid.empty);
  const [idCategorieClient, setIdCategorieClient] = useState<Guid>(Guid.empty);
  const [idEquipement, setIdEquipement] = useState<Guid>(Guid.empty);
  const [tranchesAges, setTranchesAges] = useState<ITrancheAge[]>([]);
  const [detailsPrix, setDetailsPrix] = useState<IPlanDetailTarifRow[]>([]);
  const [isLoadingTarifs, setIsLoadingTarifs] = useState(false);

  const { getData: getTrancheAges, postData } = useApi<ITrancheAge[] | IPlanDetailTarifRow[]>('trancheAge');
  const { data: trancheAgesData } = useQuery({
    queryKey: ['ITrancheAge-calculateur'],
    queryFn: () => getTrancheAges({ customPath: 'trancheAge/getasync', returnOnly: true }) as Promise<ITrancheAge[]>,
  });

  const { editorOptions: planEditorOptions } = useLookUpBase({
    controllerName: 'planentete',
    showFind: false,
    showClear: true,
    isModeModifier: true,
  });
  const { editorOptions: uniteEditorOptions } = useLookUpBase({
    controllerName: 'unite',
    showFind: false,
    showClear: true,
    isModeModifier: true,
  });
  const { editorOptions: categorieClientEditorOptions } = useLookUpBase({
    controllerName: 'categorieclient',
    showFind: false,
    showClear: true,
    isModeModifier: true,
  });
  const { editorOptions: equipementEditorOptions } = useLookUpBase({
    controllerName: 'uniteequipmenttype',
    showFind: false,
    showClear: true,
    isModeModifier: true,
  });

  useEffect(() => {
    if (!trancheAgesData) return;
    const actives = (Array.isArray(trancheAgesData) ? trancheAgesData : [])
      .filter((t) => t.actif)
      .sort((a, b) => b.ageDebut - a.ageDebut);
    setTranchesAges(actives.map((tranche, index) => {
      const isFirstAdult = tranche.isAdulte && !actives.slice(0, index).some((t) => t.isAdulte);
      return { ...tranche, nombrePersonnes: isFirstAdult ? 1 : 0 };
    }));
  }, [trancheAgesData]);

  const closePopup = useCallback(() => {
    setIsVisible(false);
    navigate(-1);
  }, [navigate]);

  const fermerOptions = useMemo(() => ({
    text: 'Fermer',
    onClick: closePopup,
  }), [closePopup]);

  const totalSousTotal = useMemo(
    () => detailsPrix.reduce((sum, row) => sum + (Number(row.sousTotal) || 0), 0),
    [detailsPrix],
  );
  const totalHebergement = useMemo(
    () => detailsPrix.reduce((sum, row) => sum + (Number(row.montantHebergementTaxable) || 0), 0),
    [detailsPrix],
  );

  const trancheAgeLookup = useMemo(() => (
    tranchesAges.map((t) => ({
      id: t.idTrancheAge,
      description: formatTrancheAgeLabel(t),
    }))
  ), [tranchesAges]);

  const updateTrancheQuantite = useCallback((idTrancheAge: Guid, value: number) => {
    setTranchesAges((prev) => prev.map((tranche) => (
      tranche.idTrancheAge.equals(idTrancheAge)
        ? { ...tranche, nombrePersonnes: value ?? 0 }
        : tranche
    )));
  }, []);

  const obtenirLesTarifs = useCallback(async () => {
    if (!idPlanEntete || idPlanEntete.isEmpty()) {
      notify('Veuillez sélectionner un plan.', 'warning', 3000);
      return;
    }
    if (!idUnite || idUnite.isEmpty()) {
      notify('Veuillez sélectionner une unité.', 'warning', 3000);
      return;
    }
    const totalPersonnes = tranchesAges.reduce((sum, t) => sum + (t.nombrePersonnes || 0), 0);
    if (totalPersonnes <= 0) {
      notify('Veuillez indiquer au moins une personne.', 'warning', 3000);
      return;
    }

    const debut = combineDateAndTime(dateDebut, heureDebut);
    const fin = combineDateAndTime(dateFin, heureFin);
    if (fin < debut) {
      notify('La date de fin doit être postérieure à la date de début.', 'warning', 3000);
      return;
    }

    setIsLoadingTarifs(true);
    try {
      const result = await postData({
        id_plan_entete: idPlanEntete.toString(),
        id_unite: idUnite.toString(),
        date_debut: debut,
        date_fin: fin,
        statut_membre: statutMembre,
        statut_resident: statutResident,
        id_categorie_client: idCategorieClient?.isEmpty?.() ? null : idCategorieClient?.toString?.() ?? null,
        id_equipement: idEquipement?.isEmpty?.() ? null : idEquipement?.toString?.() ?? null,
        tranche_ages: tranchesAges
          .filter((t) => (t.nombrePersonnes || 0) > 0)
          .map((t) => ({
            idTrancheAge: t.idTrancheAge.toString(),
            id_tranche_age: t.idTrancheAge.toString(),
            quantite: t.nombrePersonnes || 0,
            nombrePersonnes: t.nombrePersonnes || 0,
            ageDebut: t.ageDebut,
            ageFin: t.ageFin,
            isAdulte: t.isAdulte,
            actif: t.actif,
          })),
      }, 'PlanDetailTarif/GetTarifs', true) as IPlanDetailTarifRow[];

      setDetailsPrix(Array.isArray(result) ? result : []);
    } catch (error: any) {
      notify(error?.message || 'Impossible d\'obtenir les tarifs.', 'error', 4000);
      setDetailsPrix([]);
    } finally {
      setIsLoadingTarifs(false);
    }
  }, [
    idPlanEntete,
    idUnite,
    idCategorieClient,
    idEquipement,
    dateDebut,
    heureDebut,
    dateFin,
    heureFin,
    statutMembre,
    statutResident,
    tranchesAges,
    postData,
  ]);

  if (!isVisible) return null;

  return (
    <Popup
      visible={isVisible}
      dragEnabled={true}
      hideOnOutsideClick={false}
      showCloseButton={true}
      showTitle={true}
      title="Calculateur de tarifs web"
      container=".dx-viewport"
      width={1300}
      height={600}
      onHiding={closePopup}
    >
      <div className="calculateur-tarifs-web">
        <div className="calculateur-tarifs-web__body">
          <div className="calculateur-tarifs-web__filters">
            <div className="calculateur-tarifs-web__filter-row">
              <span className="calculateur-tarifs-web__label">Date début</span>
              <DateBox
                type="date"
                displayFormat="dd-MM-yyyy"
                value={dateDebut}
                onValueChanged={(e) => e.value && setDateDebut(e.value)}
              />
              <DateBox
                type="time"
                displayFormat="HH:mm"
                value={heureDebut}
                onValueChanged={(e) => e.value && setHeureDebut(e.value)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row">
              <span className="calculateur-tarifs-web__label">Date fin</span>
              <DateBox
                type="date"
                displayFormat="dd-MM-yyyy"
                value={dateFin}
                onValueChanged={(e) => e.value && setDateFin(e.value)}
              />
              <DateBox
                type="time"
                displayFormat="HH:mm"
                value={heureFin}
                onValueChanged={(e) => e.value && setHeureFin(e.value)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--radio">
              <span className="calculateur-tarifs-web__label">Statut Résident</span>
              <RadioGroup
                items={STATUT_RESIDENT}
                valueExpr="id"
                displayExpr="description"
                value={statutResident}
                layout="horizontal"
                onValueChanged={(e) => setStatutResident(e.value)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--radio">
              <span className="calculateur-tarifs-web__label">Statut membre</span>
              <RadioGroup
                items={STATUT_MEMBRE}
                valueExpr="id"
                displayExpr="description"
                value={statutMembre}
                layout="horizontal"
                onValueChanged={(e) => setStatutMembre(e.value)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--select">
              <span className="calculateur-tarifs-web__label">Plan</span>
              <SelectBox
                dataSource={planEditorOptions.dataSource}
                displayExpr={planEditorOptions.displayExpr}
                valueExpr={planEditorOptions.valueExpr}
                searchEnabled={true}
                value={idPlanEntete}
                onValueChanged={(e) => setIdPlanEntete(e.value ?? Guid.empty)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--select">
              <span className="calculateur-tarifs-web__label">Unité</span>
              <SelectBox
                dataSource={uniteEditorOptions.dataSource}
                displayExpr={uniteEditorOptions.displayExpr}
                valueExpr={uniteEditorOptions.valueExpr}
                searchEnabled={true}
                value={idUnite}
                onValueChanged={(e) => setIdUnite(e.value ?? Guid.empty)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--select">
              <span className="calculateur-tarifs-web__label">Catégorie de client</span>
              <SelectBox
                dataSource={categorieClientEditorOptions.dataSource}
                displayExpr={categorieClientEditorOptions.displayExpr}
                valueExpr={categorieClientEditorOptions.valueExpr}
                searchEnabled={true}
                value={idCategorieClient}
                onValueChanged={(e) => setIdCategorieClient(e.value ?? Guid.empty)}
              />
            </div>

            <div className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--select">
              <span className="calculateur-tarifs-web__label">Équipement</span>
              <SelectBox
                dataSource={equipementEditorOptions.dataSource}
                displayExpr={equipementEditorOptions.displayExpr}
                valueExpr={equipementEditorOptions.valueExpr}
                searchEnabled={true}
                value={idEquipement}
                onValueChanged={(e) => setIdEquipement(e.value ?? Guid.empty)}
              />
            </div>

            {tranchesAges.map((tranche) => (
              <div
                key={tranche.idTrancheAge.toString()}
                className="calculateur-tarifs-web__filter-row calculateur-tarifs-web__filter-row--qty"
              >
                <span className="calculateur-tarifs-web__label">{formatTrancheAgeLabel(tranche)}</span>
                <NumberBox
                  className="calculateur-tarifs-web__qty-input"
                  value={tranche.nombrePersonnes || 0}
                  min={0}
                  showSpinButtons={true}
                  onValueChanged={(e) => updateTrancheQuantite(tranche.idTrancheAge, e.value)}
                />
              </div>
            ))}

            <Button
              className="calculateur-tarifs-web__obtenir"
              text="Obtenir les tarifs"
              type="default"
              stylingMode="contained"
              disabled={isLoadingTarifs}
              onClick={obtenirLesTarifs}
            />
          </div>

          <div className="calculateur-tarifs-web__results">
            <div className="calculateur-tarifs-web__results-title">Détails de prix</div>
            <DataGrid
              dataSource={detailsPrix}
              showBorders={true}
              showColumnLines={true}
              showRowLines={true}
              allowColumnResizing={true}
              columnAutoWidth={true}
              height="100%"
              noDataText={isLoadingTarifs ? 'Chargement...' : ''}
            >
              <Scrolling mode="virtual" />
              <Paging enabled={false} />
              <Column dataField="description" caption="Description" />
              <Column dataField="dateDebut" caption="Date de début" dataType="date" format="yyyy-MM-dd" />
              <Column dataField="dateFin" caption="Date de fin" dataType="date" format="yyyy-MM-dd" />
              <Column
                dataField="idTrancheAge"
                caption="Tranche d'âge"
                calculateDisplayValue={(row: IPlanDetailTarifRow) => {
                  const match = trancheAgeLookup.find((t) => t.id.equals(row.idTrancheAge));
                  if (match) return match.description;
                  if (row.ageFin != null && row.ageFin >= 99) return `${row.ageFin} ans et plus`;
                  return '';
                }}
              />
              <Column dataField="nombrePersonnes" caption="Nombre Pers." dataType="number" />
              <Column dataField="duree" caption="Durée" dataType="number" />
              <Column dataField="quantite" caption="Quantité" dataType="number" />
              <Column dataField="prix" caption="Prix" dataType="number" format="#,##0.00" />
              <Column dataField="sousTotal" caption="Sous-Total" dataType="number" format="#,##0.00" />
              <Column dataField="montantHebergementTaxable" caption="Montant Héb." dataType="number" format="#,##0.00" />
              <Column
                dataField="typeDetail"
                caption="Type de détail"
                calculateDisplayValue={(row: IPlanDetailTarifRow) => TYPE_DETAIL_LABELS[row.typeDetail] ?? String(row.typeDetail ?? '')}
              />
            </DataGrid>

            <div className="calculateur-tarifs-web__totals">
              <TextBox value={totalSousTotal.toFixed(2)} readOnly={true} />
              <TextBox value={totalHebergement.toFixed(2)} readOnly={true} />
            </div>
          </div>
        </div>
      </div>

      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={fermerOptions}
      />
    </Popup>
  );
}
